import { ArrowRight, ChevronRight, DollarSign, Eye, Layers, Package, Palette } from "lucide-react";
import { Link, Navigate } from "react-router";
import { useSetPageHeader } from "@/lib/page-header";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { NoAccessBlock } from "@/components/shared/NoAccessBlock";
import { countByShadeSystem, type Shade } from "@/components/catalogo/shade-groups";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { CATALOGO_HUB_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";

const cardCls = "rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card";

type Row = { label: string; count?: number };

function listActiveServices(items: ServiceItem[]): Row[] {
  return items
    .filter((s) => s.active)
    .map((s) => ({ label: s.label }))
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

/** O que o dentista escolhe ao pedir. O cadastro de insumo saiu do catálogo em 30/09 e o
 *  cartão passou a contar a oferta, não a prateleira. */
function listarOfertas(items: { label: string; active: boolean }[]): Row[] {
  return items
    .filter((m) => m.active)
    .map((m) => ({ label: m.label }))
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

type ServiceItem = { active: boolean; label: string };
type PriceItem = { serviceCategory: { label: string } };

function countPricesByService(items: PriceItem[]): Row[] {
  const map = new Map<string, number>();
  for (const item of items) {
    map.set(item.serviceCategory.label, (map.get(item.serviceCategory.label) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

export function CatalogoHub() {
  useTourDaPagina(CATALOGO_HUB_TOUR_STEPS);
  useSetPageHeader(
    {
      title: "Catálogo",
      subtitle: "Resumo do que está ativo — serviços, materiais e cores usados nos pedidos dos dentistas",
    },
    [],
  );

  const meQuery = trpc.me.current.useQuery();
  const canManage = !!meQuery.data?.activeOrganization?.permissions?.["labCatalog.manage"];

  const servicesQuery = trpc.prosthesis.catalog.myServiceCategories.useQuery();
  const materialsQuery = trpc.prosthesis.catalog.materialOptions.useQuery();
  const shadesQuery = trpc.prosthesis.catalog.myShadeOptions.useQuery();
  const podeVerPrecos = usePermissao("prices.view");
  const pricesQuery = trpc.labPricing.priceList.list.useQuery(undefined, { enabled: podeVerPrecos });

  if (meQuery.data && !canManage && podeVerPrecos) return <Navigate to="/lab/catalogo/gerenciar#precos" replace />;
  if (meQuery.data && !canManage) {
    return <NoAccessBlock description="O catálogo do laboratório é restrito a quem tem permissão de gerenciá-lo." />;
  }

  const servicesRows = listActiveServices((servicesQuery.data ?? []) as ServiceItem[]);
  const materialsRows = listarOfertas((materialsQuery.data ?? []) as { label: string; active: boolean }[]);
  const shadesRows = countByShadeSystem((shadesQuery.data ?? []) as Shade[]).map((g) => ({
    label: g.label,
    count: g.count,
  }));
  const pricesRows = countPricesByService((pricesQuery.data ?? []) as PriceItem[]);

  // Contagem para o hub do celular — soma o que já está carregado, sem query nova.
  const somar = (rows: Row[]) =>
    rows.some((r) => r.count !== undefined)
      ? rows.reduce((a, r) => a + (r.count ?? 0), 0)
      : rows.length;
  const plural = (n: number, um: string, muitos: string, nenhum: string) =>
    n === 0 ? nenhum : `${n} ${n === 1 ? um : muitos}`;

  const secoes = [
    {
      icon: Package,
      accent: "#0ea5e9",
      title: "Serviços",
      descricao: "Gerencie os serviços oferecidos pelo laboratório.",
      resumo: plural(somar(servicesRows), "serviço ativo", "serviços ativos", "Nenhum serviço ativo"),
      href: "/lab/catalogo/gerenciar#servicos",
    },
    {
      icon: Layers,
      accent: "#d946ef",
      title: "Materiais",
      descricao: "O que o dentista escolhe ao pedir. Insumos e preços de compra ficam em Estoque.",
      resumo: plural(materialsRows.length, "material ofertado", "materiais ofertados", "Nenhum material ofertado"),
      href: "/lab/catalogo/gerenciar#materiais",
    },
    {
      icon: Palette,
      accent: "#2457ff",
      title: "Cores",
      descricao: "Organize as escalas e opções de cores.",
      resumo: plural(shadesRows.length, "escala de cores", "escalas de cores", "Nenhuma escala de cores"),
      href: "/lab/catalogo/gerenciar#cores",
    },
    {
      icon: DollarSign,
      accent: "#10b981",
      title: "Preços",
      descricao: "Defina os valores dos serviços e materiais.",
      resumo: plural(somar(pricesRows), "preço cadastrado", "preços cadastrados", "Nenhum preço cadastrado"),
      href: "/lab/catalogo/gerenciar#precos",
    },
  ];

  return (
    <>
      {/* O catálogo como o dentista vê (e o PDF pra mandar) — pedido do Jun. */}
      <div className="mx-auto mb-3 flex w-full max-w-[1400px] justify-end">
        <Link
          to="/lab/catalogo/visualizar"
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-brand-700 shadow-sm hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-primary"
        >
          <Eye className="h-4 w-4" />
          Visualizar catálogo
        </Link>
      </div>
      {/* Celular: hub de navegação. Empilhar as quatro listas completas fazia a tela virar um
          rolar interminável antes de se chegar a qualquer ação — o catálogo é ponto de partida,
          não relatório. Cada cartão inteiro é o link; nada de um "Ver X" separado dentro dele.
          Do `md` pra cima, os cartões de resumo seguem exatamente como estão. */}
      <ul className="mx-auto flex w-full max-w-[1400px] flex-col gap-3 md:hidden">
        {secoes.map(({ icon: Icon, accent, title, descricao, resumo, href }) => (
          <li key={title}>
            <Link
              to={href}
              className="flex min-h-[88px] items-center gap-3 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm transition-colors active:bg-zinc-50 dark:border-border dark:bg-card dark:active:bg-muted/50"
            >
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                style={{ backgroundColor: `${accent}1a`, color: accent }}
              >
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-zinc-900 dark:text-foreground">
                  {title}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-zinc-500 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden dark:text-muted-foreground">
                  {descricao}
                </span>
                <span className="mt-1 block text-xs font-medium text-zinc-700 dark:text-foreground/80">
                  {resumo}
                </span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-zinc-400 dark:text-muted-foreground" />
            </Link>
          </li>
        ))}
      </ul>

    <div className="mx-auto hidden w-full max-w-[1400px] gap-4 md:grid md:grid-cols-2 lg:grid-cols-4">
      <SummaryCard
        cardDataTour="catalogo-hub-servicos"
        icon={Package}
        accent="#0ea5e9"
        title="Serviços"
        rows={servicesRows}
        href="/lab/catalogo/gerenciar#servicos"
      />
      <SummaryCard
        cardDataTour="catalogo-hub-materiais"
        icon={Layers}
        accent="#d946ef"
        title="Materiais"
        rows={materialsRows}
        href="/lab/catalogo/gerenciar#materiais"
      />
      <SummaryCard
        cardDataTour="catalogo-hub-cores"
        icon={Palette}
        accent="#2457ff"
        title="Cores"
        rows={shadesRows}
        href="/lab/catalogo/gerenciar#cores"
      />
      <SummaryCard
        cardDataTour="catalogo-hub-precos"
        icon={DollarSign}
        accent="#10b981"
        title="Preços"
        rows={pricesRows}
        href="/lab/catalogo/gerenciar#precos"
      />
    </div>
    </>
  );
}

function SummaryCard({
  icon: Icon,
  accent,
  title,
  rows,
  href,
  cardDataTour,
}: {
  icon: typeof Package;
  accent: string;
  title: string;
  rows: Row[];
  href: string;
  cardDataTour?: string;
}) {
  return (
    // O cartão inteiro é o link, como já era no celular: o alvo útil é o cartão, e obrigar a mirar
    // no "Ver X" de 90px era pedir precisão sem motivo. O "Ver X" continua no rodapé como
    // afordância — sem ele o cartão não se anuncia como clicável.
    <Link
      to={href}
      data-tour={cardDataTour}
      className={`group block transition-colors hover:border-brand-300 dark:hover:border-primary/40 ${cardCls}`}
    >
      <div className="flex items-center gap-2">
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: `${accent}1a`, color: accent }}
        >
          <Icon className="h-4 w-4" />
        </span>
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-foreground">{title}</h2>
      </div>

      {rows.length === 0 ? (
        <p className="mt-3 text-xs text-zinc-400 dark:text-muted-foreground">Nenhum item ativo ainda.</p>
      ) : (
        <ul className="mt-3 space-y-1">
          {rows.map((row) => (
            <li
              key={row.label}
              className="flex items-center justify-between text-sm text-zinc-600 dark:text-muted-foreground"
            >
              <span>{row.label}</span>
              {row.count !== undefined && (
                <span className="font-semibold tabular-nums text-zinc-900 dark:text-foreground">{row.count}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-600 group-hover:underline dark:text-primary">
        Ver {title}
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}
